import React, { createContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import useMode from './useMode.js';

const ThemeContext = /*#__PURE__*/createContext({});
function ThemeProvider({
  children,
  defaultMode,
  storageId,
  getModeFromStore,
  setModeToStore
}) {
  const {
    modeSetting,
    updateMode,
    toggleMode,
    modeDisplayed,
    updateWindowStyles
  } = useMode({
    defaultMode,
    storageId,
    getModeFromStore,
    setModeToStore
  });
  const memoizedValue = useMemo(() => ({
    modeSetting,
    updateMode,
    toggleMode,
    modeDisplayed,
    updateWindowStyles
  }), [modeSetting, updateMode, toggleMode, modeDisplayed, updateWindowStyles]);
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: memoizedValue
  }, children);
}
ThemeProvider.propTypes = {
  /**
   * Default mode value for uncontrolled component.
   * When user hasn't yet selected a mode (nothing is stored in `localStorage`)
   * the component will use this value instead.
   * */
  defaultMode: PropTypes.oneOf(['light', 'dark', 'auto']),
  /**
   * Override for the `localStorage` id used by Screentone.
   * Normally, you should not need to change this.
   * Ignored if `getModeFromtStore` or `setModeToStore` are defined.
   * */
  storageId: PropTypes.string,
  /**
   * Function that fetches the user settings from a store. `() => someInternalState.mode`.
   * Will default to fetching from `localStorage` if not set.
   */
  getModeFromStore: PropTypes.func,
  /**
   * Function that accepts a string and writes that into a store. `(m) => someAPI.write(m)`.
   * Will default to writing to `localStorage` if not set.
   */
  setModeToStore: PropTypes.func
};
ThemeProvider.defaultProps = {
  storageId: 'st-mode',
  defaultMode: 'auto',
  getModeFromStore: null,
  setModeToStore: null
};

export { ThemeProvider as T, ThemeContext as a };
